import { Button, Card, InputNumber, Popconfirm, message } from "antd";
import ListTable from "common/ListTable";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { alertPop } from "apiServices/common/helper";

const DetailsTable = ({ selectedSizes, polyData, setPolyData, setDuplicatePolyError, info, mode }) => {
  const [dataSource, setDataSource] = useState([]);
  const [inputErrors, setInputErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const updateTimeoutRef = useRef(null);
  const updatePolyDataRef = useRef(null);
  const [allSizeRatios, setAllSizeRatios] = useState([]); // Track all size ratios

  // Separate effect for initialization only
  useEffect(() => {
    if (selectedSizes && selectedSizes.length > 0 && !isInitialized) {
      if (polyData && polyData.length > 0) {
        // Store all size ratios from existing data
        const allRatios = [];
        polyData.forEach((poly) => {
          poly.size_ratios.forEach((ratio) => {
            allRatios.push({
              poly_id: poly.id,
              size_id: ratio.size_id,
              ...ratio,
            });
          });
        });
        setAllSizeRatios(allRatios);

        const tableData = polyData.map((poly, index) => {
          const rowData = {
            key: poly.id || Date.now() + index,
            id: poly.id || 0,
            polyNo: poly.ratio_name,
            is_delete: poly.is_delete || 0,
          };

          selectedSizes.forEach((size) => {
            const sizeRatio = poly.size_ratios.find((ratio) => ratio.size_id === size.id);
            rowData[`size_${size.id}`] = sizeRatio ? sizeRatio.quantity : 0;
            rowData[`size_${size.id}_id`] = sizeRatio ? sizeRatio.id : 0;
            rowData[`size_${size.id}_is_delete`] = sizeRatio ? sizeRatio.is_delete || 0 : 0;
          });

          return rowData;
        });

        setDataSource(tableData);
      } else {
        const initialRow = {
          key: Date.now(),
          id: 0,
          polyNo: "",
          is_delete: 0,
          ...selectedSizes.reduce(
            (acc, size) => ({
              ...acc,
              [`size_${size.id}`]: 0,
              [`size_${size.id}_id`]: 0,
              [`size_${size.id}_is_delete`]: 0,
            }),
            {},
          ),
        };
        setDataSource([initialRow]);

        setPolyData([
          {
            id: 0,
            ratio_name: initialRow.polyNo,
            is_delete: 0,
            size_ratios: selectedSizes.map((size) => ({
              id: 0,
              size_id: size.id,
              quantity: initialRow[`size_${size.id}`] || 0,
              is_delete: 0,
            })),
          },
        ]);
      }
      setInputErrors({});
      setIsInitialized(true);
    }
  }, [selectedSizes, isInitialized]);

  // Reset initialization when selectedSizes change
  useEffect(() => {
    setIsInitialized(false);
  }, [selectedSizes]);

  const generatePolyNo = useCallback(
    (record) => {
      const polyParts = [];
      selectedSizes.forEach((size) => {
        const qty = record[`size_${size.id}`] || 0;
        if (qty > 0) {
          polyParts.push(`${size.name}-${qty}`);
        }
      });
      return polyParts.join(" ");
    },
    [selectedSizes],
  );

  const isDuplicatePolyNo = useCallback((polyNo, key, currentDataSource) => {
    return currentDataSource.some((row) => row.polyNo === polyNo && row.key !== key && polyNo !== "");
  }, []);

  // Optimize updatePolyData with useCallback
  const updatePolyData = useCallback(
    (rows) => {
      const selectedSizeIds = selectedSizes.map((size) => size.id);

      const newPolyData = rows.map((row) => {
        // Get all size ratios for this poly (including previously selected sizes)
        const existingSizeRatios = allSizeRatios.filter((ratio) => ratio.poly_id === row.id);

        // Create a map of all size ratios
        const sizeRatioMap = new Map();

        // Add existing size ratios
        existingSizeRatios.forEach((ratio) => {
          sizeRatioMap.set(ratio.size_id, {
            id: ratio.id,
            size_id: ratio.size_id,
            quantity: ratio.quantity,
            is_delete: ratio.is_delete || 0,
          });
        });

        // Update with current selected sizes
        selectedSizes.forEach((size) => {
          const existingRatio = sizeRatioMap.get(size.id);
          sizeRatioMap.set(size.id, {
            id: row[`size_${size.id}_id`] || (existingRatio ? existingRatio.id : 0),
            size_id: size.id,
            quantity: row[`size_${size.id}`] || 0,
            is_delete: row[`size_${size.id}_is_delete`] || 0,
          });
        });

        // Mark as deleted any size ratios that are no longer in selectedSizes
        sizeRatioMap.forEach((ratio, sizeId) => {
          if (!selectedSizeIds.includes(sizeId)) {
            ratio.is_delete = 1;
          }
        });

        return {
          id: row.id || 0,
          ratio_name: row.polyNo,
          is_delete: row.is_delete || 0,
          size_ratios: Array.from(sizeRatioMap.values()),
        };
      });

      // Only check duplicates for non-deleted rows
      const activeRows = rows.filter((row) => !row.is_delete);
      const polyNos = activeRows.map((r) => r.polyNo).filter((p) => p && p !== "");
      const hasDuplicate = polyNos.length !== new Set(polyNos).size;
      if (typeof setDuplicatePolyError === "function") {
        setDuplicatePolyError(hasDuplicate);
      }

      setPolyData(newPolyData);
    },
    [selectedSizes, setPolyData, setDuplicatePolyError, allSizeRatios],
  );

  // Update ref when updatePolyData changes
  updatePolyDataRef.current = updatePolyData;

  // Debounced update function - use ref to access latest function
  const debouncedUpdatePolyData = useCallback((rows) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      if (updatePolyDataRef.current) {
        updatePolyDataRef.current(rows);
      }
    }, 200); // Reduced debounce time
  }, []);

  const handleQuantityChange = useCallback(
    (value, record, sizeId) => {
      setDataSource((prevData) => {
        const newData = prevData.map((item) => {
          if (item.key === record.key) {
            const updatedRecord = {
              ...item,
              [`size_${sizeId}`]: value || 0,
            };
            updatedRecord.polyNo = generatePolyNo(updatedRecord);

            if (isDuplicatePolyNo(updatedRecord.polyNo, updatedRecord.key, prevData)) {
              setInputErrors((prev) => ({
                ...prev,
                [record.key]: "Duplicate Poly No. combination is not allowed!",
              }));
              if (typeof setDuplicatePolyError === "function") {
                setDuplicatePolyError(true);
              }
            } else {
              setInputErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[record.key];
                return newErrors;
              });

              if (typeof setDuplicatePolyError === "function") {
                setDuplicatePolyError(false);
              }
            }

            return updatedRecord;
          }
          return item;
        });

        // Use debounced update for polyData
        debouncedUpdatePolyData(newData);
        return newData;
      });
    },
    [generatePolyNo, isDuplicatePolyNo, debouncedUpdatePolyData, setDuplicatePolyError],
  );

  const handleAddRow = () => {
    const newRow = {
      key: Date.now(),
      id: 0,
      polyNo: "",
      is_delete: 0,
      ...selectedSizes.reduce(
        (acc, size) => ({
          ...acc,
          [`size_${size.id}`]: 0,
          [`size_${size.id}_id`]: 0,
          [`size_${size.id}_is_delete`]: 0,
        }),
        {},
      ),
    };
    setDataSource((prev) => {
      const newData = [...prev, newRow];
      updatePolyData(newData);
      return newData;
    });

    if (typeof setDuplicatePolyError === "function") {
      setDuplicatePolyError(false);
    }
  };

  const handleDeleteRow = (key) => {
    setDataSource((prev) => {
      const newData = prev.map((item) => {
        if (item.key === key) {
          // In edit mode, mark as deleted instead of removing
          if (mode === "edit" && item.id > 0) {
            const deletedItem = { ...item, is_delete: 1 };
            // Mark all size ratios as deleted too
            selectedSizes.forEach((size) => {
              deletedItem[`size_${size.id}_is_delete`] = 1;
            });
            return deletedItem;
          }
        }
        return item;
      });

      // In non-edit mode or for new rows (id === 0), actually remove them
      const filteredData = newData.filter((item) => {
        if (mode !== "edit" || item.id === 0) {
          return item.key !== key;
        }
        return true;
      });

      updatePolyData(filteredData);

      if (typeof setDuplicatePolyError === "function") {
        // Only check duplicates for non-deleted rows
        const activeRows = filteredData.filter((row) => !row.is_delete);
        const polyNos = activeRows.map((r) => r.polyNo).filter((p) => p && p !== "");
        setDuplicatePolyError(polyNos.length !== new Set(polyNos).size);
      }

      return filteredData;
    });
  };

  const columns = [
    {
      title: "Poly No.",
      dataIndex: "polyNo",
      key: "polyNo",
      width: 180,
      fixed: "left",
      render: (text) => <span style={{ fontWeight: 500, color: "#1890ff" }}>{text || "-"}</span>,
    },
    ...selectedSizes.map((size) => ({
      title: size.name,
      dataIndex: `size_${size.id}`,
      key: `size_${size.id}`,
      width: 10,
      render: (value, record) => (
        <InputNumber
          key={`${record.key}-${size.id}`} // Add stable key
          min={0}
          value={value}
          disabled={mode === "view"}
          onChange={(val) => handleQuantityChange(val, record, size.id)}
          size="small"
          style={{
            width: "100px",
            padding: "0px 0px",
          }}
          status={inputErrors[record.key] ? "error" : ""}
          controls={false}
          keyboard={true}
          onKeyDown={(e) => {
            // Allow: backspace, delete, tab, escape, enter
            if (
              [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
              // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
              (e.keyCode === 65 && e.ctrlKey === true) ||
              (e.keyCode === 67 && e.ctrlKey === true) ||
              (e.keyCode === 86 && e.ctrlKey === true) ||
              (e.keyCode === 88 && e.ctrlKey === true)
            ) {
              return;
            }
            // Ensure that it is a number and stop the keypress
            if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
              e.preventDefault();
            }
          }}
          parser={(value) => value.replace(/\D/g, "")}
          formatter={(value) => value}
        />
      ),
    })),
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Popconfirm
          title="Are you sure to delete this row?"
          onConfirm={() => handleDeleteRow(record.key)}
          okText="Yes"
          cancelText="No"
          disabled={dataSource.length === 1 || mode === "view"}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={dataSource.length === 1 || mode === "view"}
            size="small"
          >
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Card
      title="Details Table"
      className="mt-3"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRow} size="small">
          Add More
        </Button>
      }
      bodyStyle={{ padding: "12px" }}
    >
      <div className="table" style={{ marginTop: 10 }}>
        <ListTable
          dataSource={dataSource.filter((row) => !row.is_delete)}
          columns={columns}
          pagination={false}
          scroll={{ x: "max-content", y: 360 }}
          bordered
          size="small"
          sticky
          emptyText="No records to show yet."
          style={{ fontSize: "10px" }}
          rowClassName={(record) => (inputErrors[record.key] ? "ant-table-row-error" : "")}
        />
        {/* Show error below the table row if exists */}
        {Object.entries(inputErrors).map(([key, error]) => (
          <div key={key} style={{ color: "#ff4d4f", fontSize: 14, marginTop: 2 }}>
            {error}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default DetailsTable;
